/**
 * Ключевые показатели территории — вертикальный стек карточек (§3 FE-15).
 *
 * Принимает данные через props (fetchит родитель — ContextSidebar).
 * Форматирование чисел через Intl.NumberFormat('ru-RU').
 * Карточки видны всегда: показатель без значения (null / нет данных по КАТО)
 * рендерится названием и иконкой — набор карточек стабилен при смене КАТО.
 */
import {
  ArrowLeftRight,
  Building2,
  MapPin,
  UserSearch,
  Users,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Indicator } from './Indicator';
import type { GeneralInfo } from '../../../types/public';

/** Ключи показателей = поля GeneralInfo, по которым есть карточки. */
export type IndicatorTrends = Partial<
  Record<
    | 'population'
    | 'village_count'
    | 'saldo_migration'
    | 'avg_salary'
    | 'unemployed'
    | 'area_km2',
    number | null
  >
>;

interface IndicatorsProps {
  general?: GeneralInfo;
  /** Чипы динамики (API их пока не отдаёт — приходят из hardData-мока). */
  trends?: IndicatorTrends;
}

const fmt = (v: number | null | undefined): string | null =>
  v != null ? Intl.NumberFormat('ru-RU').format(v) : null;

/** Территория: не больше одного знака после запятой (замечание ревью). */
const fmt1 = (v: number | null | undefined): string | null =>
  v != null
    ? Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v)
    : null;

export const Indicators = ({ general, trends }: IndicatorsProps) => {
  const { t } = useTranslation();

  const saldo = general?.saldo_migration;

  // Чип тренда показывается только вместе со значением показателя.
  const trendOf = (
    key: keyof IndicatorTrends,
    value: number | null | undefined,
  ): number | null => (value != null ? (trends?.[key] ?? null) : null);

  return (
    <div className="flex flex-col gap-3" data-testid="indicators-list">
      <Indicator
        icon={Users}
        label={t('menu.population')}
        value={fmt(general?.population)}
        unit={t('units.people')}
        accent="green"
        trend={trendOf('population', general?.population)}
        testId="indicator-population"
      />
      <Indicator
        icon={Building2}
        label={t('menu.snpCount')}
        value={fmt(general?.village_count)}
        unit={t('units.snp')}
        accent="blue"
        trend={trendOf('village_count', general?.village_count)}
        testId="indicator-village_count"
      />
      <Indicator
        icon={ArrowLeftRight}
        label={t('menu.saldoMigration')}
        value={fmt(saldo)}
        unit={(saldo ?? 0) < 0 ? t('units.outflow') : t('units.inflow')}
        accent={(saldo ?? 0) < 0 ? 'danger' : 'green'}
        trend={trendOf('saldo_migration', saldo)}
        testId="indicator-saldo_migration"
      />
      <Indicator
        icon={Wallet}
        label={t('menu.avgSalary')}
        value={fmt(general?.avg_salary)}
        unit={t('units.tenge')}
        accent="green"
        trend={trendOf('avg_salary', general?.avg_salary)}
        testId="indicator-avg_salary"
      />
      <Indicator
        icon={UserSearch}
        label={t('menu.unemployed')}
        value={fmt(general?.unemployed)}
        unit={t('units.people')}
        accent="green"
        trend={trendOf('unemployed', general?.unemployed)}
        testId="indicator-unemployed"
      />
      <Indicator
        icon={MapPin}
        label={t('menu.area')}
        value={fmt1(general?.area_km2)}
        unit={t('units.km2')}
        accent="blue"
        trend={trendOf('area_km2', general?.area_km2)}
        testId="indicator-area_km2"
      />
    </div>
  );
};
